import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './style.css';

/**
 * SpendTypeDropdown Component
 *
 * @param {Object} props
 * @param {Object} props.data - Current XML line item data
 * @param {Function} props.onSelected - Callback when a spend type is selected (spendType, lineData)
 * @param {Function} [props.onChange] - Callback on input change
 * @param {Object} [props.style] - Custom styles for the input
 * @param {Array} props.spendTypes - Shared list of spend types from parent
 * @param {boolean} props.loading - Loading state for spend types
 * @param {Function} props.onCreate - Callback to trigger "Create New Spend Type" flow
 */
const SpendTypeDropdown = ({
  data,
  onSelected,
  onChange = undefined,
  style = {},
  spendTypes = [],
  loading = false,
  onCreate
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const dropdownRef = useRef(null);
  const listRef = useRef(null);
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0 });

  const [filteredSpendTypes, setFilteredSpendTypes] = useState(spendTypes);

  useEffect(() => {
    const list = Array.isArray(spendTypes) ? spendTypes : (spendTypes?.data || []);
    if (!query) {
      setFilteredSpendTypes(list);
    } else {
      const lowerQuery = query.toLowerCase();
      setFilteredSpendTypes(list.filter(st => st && st.name && String(st.name).toLowerCase().includes(lowerQuery)));
    }
  }, [spendTypes, query]);

  useEffect(() => {
    if (open && dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width
      });
    }
  }, [open, query, filteredSpendTypes]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        (!listRef.current || !listRef.current.contains(e.target))
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  useEffect(() => {
    const onScroll = (e) => {
      if (!open) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', onScroll, true);
    return () => window.removeEventListener('scroll', onScroll, true);
  }, [open]);

  function selectSpendType(spendType) {
    onSelected?.(spendType, data);
    setQuery('');
    setOpen(false);
    setIsFocused(false);
    dropdownRef.current?.querySelector('input')?.blur();
  }

  function handleChange(e) {
    const input = e.target.value;
    setQuery(input);
    setOpen(true);
    if (!input) {
      onSelected?.(null, data);
    }
    onChange?.(e);
  }

  const handleFocus = (e) => {
    setIsFocused(true);
    setOpen(true);
    setQuery(data?.relatedItem?.name || '');
    const inputEl = e.target;
    setTimeout(() => {
      inputEl?.select?.();
    }, 10);
  };

  const handleBlur = () => {
    setTimeout(() => {
      if (!open) {
        setIsFocused(false);
        setQuery('');
      }
    }, 150);
  };

  const displayValue = isFocused
    ? query
    : (data?.relatedItem ? data.relatedItem.name : query);

  return (
    <>
      <div ref={dropdownRef} className="product-search-dropdown-layout" onClick={(e) => e.stopPropagation()}>
        <input
          style={{ ...style, flex: 1 }}
          className={`product-search-input ${data?.matchStatus || ''}`}
          type="text"
          placeholder="Buscar tipo de gasto..."
          value={displayValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        {loading && (!data?.matchStatus || data.matchStatus === 'pending') && (
          <div className="match-loading-spinner" />
        )}
      </div>

      {open && createPortal(
        <ul
          ref={listRef}
          className="product-search-list"
          style={{
            top: coords.top,
            left: coords.left,
            width: coords.width,
            zIndex: 10005
          }}
        >
          {loading ? (
            <li className="product-search-empty">Cargando tipos de gasto...</li>
          ) : (
            <>
              {onCreate && (
                <li
                  className="product-search-item create-new"
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    onCreate?.(data);
                    setOpen(false);
                  }}
                  style={{
                    borderBottom: '1px solid var(--color-gray-20)',
                    color: 'var(--color-primary)',
                    fontWeight: 'bold'
                  }}
                >
                  ＋ Crear "{query || data?.description || 'nuevo tipo de gasto'}" como nuevo
                </li>
              )}
              {filteredSpendTypes.length === 0 ? (
                <li className="product-search-empty">No se encontraron tipos de gasto</li>
              ) : (
                filteredSpendTypes.map((st, i) => (
                  <li
                    key={st.id ?? i}
                    className="product-search-item"
                    onMouseDown={(ev) => {
                      ev.preventDefault();
                      selectSpendType(st);
                    }}
                  >
                    <div className="item-label">
                      {st.name}
                    </div>
                  </li>
                ))
              )}
            </>
          )}
        </ul>,
        document.body
      )}
    </>
  );
};

export default SpendTypeDropdown;
